/**
 * Sharing one thing by link. Two sides:
 *
 * - the workspace's (admins): make a link, add people, change or turn it off,
 *   see who opened it — /api/v1/tenants/{tid}/shares, with the workspace login;
 * - a viewer's: open a link, get a code, sign in — /api/v1/shared/{link}, with
 *   no workspace login at all. A viewer's token is kept for that one link only,
 *   in this tab's session, and never touches the workspace login.
 */
import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type { TenantBranding } from '../types/branding';

export type ShareMode = 'named' | 'anyone';
export type ShareState = 'live' | 'expired' | 'revoked' | 'gone';

export interface ShareViewerRow {
  viewer_id: string;
  email: string;
  name: string | null;
  invited: boolean;
  workspace_user: boolean;
  added_at: string | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  /** How many times they have opened it. */
  times_opened: number;
  removed: boolean;
}

export interface ShareEventRow {
  at: string;
  kind: string;
  email: string | null;
  detail: string | null;
}

export interface Share {
  share_id: string;
  kind: string;
  target_id: string;
  target_name: string | null;
  options: Record<string, unknown>;
  link: string;
  mode: ShareMode;
  allow_export: boolean;
  created_at: string;
  /** Null when the link has no end date. */
  expires_at: string | null;
  never_expires: boolean;
  revoked_at: string | null;
  state: ShareState;
  viewers: ShareViewerRow[];
  events?: ShareEventRow[];
}

export interface SharedViewerRow extends ShareViewerRow {
  share_id: string;
  kind: string;
  target_name: string | null;
  share_state: 'live' | 'expired' | 'revoked';
}

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    const error = new Error(typeof detail === 'string' ? detail : fallback) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return response.json() as Promise<T>;
}

// --- The workspace's side ------------------------------------------------------

function workspace(path: string): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return buildApiUrl(`${tenantApiPrefix(tid)}/shares${path}`);
}

function workspaceInit(method: string, body?: unknown): RequestInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    method,
    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  };
}

export async function listShares(kind: string, targetId: string): Promise<Share[]> {
  const qs = new URLSearchParams({ kind, target_id: targetId });
  const data = await readJson<{ shares: Share[] }>(
    await fetch(workspace(`?${qs.toString()}`), workspaceInit('GET')), 'Could not load the links');
  return data.shares;
}

export async function getShare(shareId: string): Promise<Share> {
  return readJson(await fetch(workspace(`/${shareId}`), workspaceInit('GET')), 'Could not load the link');
}

export async function createShare(body: {
  kind: string; target_id: string; options?: Record<string, unknown>; mode: ShareMode;
  allow_export?: boolean; days: number; emails: string[];
}): Promise<Share> {
  return readJson(await fetch(workspace(''), workspaceInit('POST', body)), 'Could not make the link');
}

// One request per thing at a time: the Share window asks as it opens, and a
// window opened twice in a moment (React's development double-mount, a double
// click) must end up with one link, not two.
const ensuring = new Map<string, Promise<Share>>();

/** The link that opens this, making one if there is none: for the Share window, as it opens. */
export function ensureShare(kind: string, targetId: string, options: Record<string, unknown> = {}): Promise<Share> {
  const key = `${kind}:${targetId}:${JSON.stringify(options)}`;
  const running = ensuring.get(key);
  if (running) return running;
  const made = (async () => readJson<Share>(
    await fetch(workspace(''), workspaceInit('POST', { kind, target_id: targetId, options, reuse: true })),
    'Could not make the link',
  ))().finally(() => ensuring.delete(key));
  ensuring.set(key, made);
  return made;
}

export async function changeShare(shareId: string, change: { mode?: ShareMode; allow_export?: boolean; days?: number; never?: boolean }): Promise<Share> {
  return readJson(await fetch(workspace(`/${shareId}`), workspaceInit('PATCH', change)), 'Could not change the link');
}

export async function addShareViewers(shareId: string, emails: string[]): Promise<Share> {
  return readJson(await fetch(workspace(`/${shareId}/viewers`), workspaceInit('POST', { emails })), 'Could not add them');
}

export async function removeShareViewer(shareId: string, viewerId: string): Promise<Share> {
  return readJson(await fetch(workspace(`/${shareId}/viewers/${viewerId}`), workspaceInit('DELETE')),
    'Could not remove them');
}

export async function revokeShare(shareId: string): Promise<Share> {
  return readJson(await fetch(workspace(`/${shareId}/revoke`), workspaceInit('POST')), 'Could not turn the link off');
}

export async function listSharedViewers(): Promise<SharedViewerRow[]> {
  const data = await readJson<{ viewers: SharedViewerRow[] }>(
    await fetch(workspace('/viewers'), workspaceInit('GET')), 'Could not load shared viewers');
  return data.viewers;
}

// --- A viewer's side -----------------------------------------------------------

export interface SharedTarget {
  kind: string;
  name: string | null;
  options: { view?: string } & Record<string, unknown>;
  allow_export: boolean;
  workspace: string;
  viewer: string;
  expires_at: string;
  dashboard?: {
    dashboard_id: string; name: string; description: string | null;
    dashboard_type: 'agent' | 'manual'; spec_key: string | null; board_kind: string | null;
  };
}

const shared = (link: string, path = '') => buildApiUrl(`/api/v1/shared/${encodeURIComponent(link)}${path}`);
const post = (body: unknown): RequestInit => ({
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
});

export async function openSharedLink(link: string): Promise<{
  workspace: string; kind: string; name: string | null; shared_by: string | null;
  /** The sharing workspace's name, logo and colours, to draw the page in. */
  branding: TenantBranding | null;
}> {
  return readJson(await fetch(shared(link)), 'This link has expired or been turned off.');
}

export async function requestShareCode(link: string, email: string): Promise<{ message: string }> {
  return readJson(await fetch(shared(link, '/code'), post({ email })), 'Could not send a code. Try again in a minute.');
}

export async function verifyShareCode(link: string, email: string, code: string): Promise<{ token: string; expires_at: string; email: string }> {
  return readJson(await fetch(shared(link, '/verify'), post({ email, code })), 'That code is not right.');
}

export async function fetchSharedTarget(link: string, token: string): Promise<SharedTarget> {
  return readJson(await fetch(shared(link, '/target'), { headers: { Authorization: `Bearer ${token}` } }),
    'Could not open this.');
}

/** Where a shared dashboard's own routes are, for setSharedBoardTarget. */
export function sharedDashboardBase(link: string): string {
  return `/api/v1/shared/${encodeURIComponent(link)}/dashboard`;
}

// The viewer's sign-in for one link, kept in this browser until it runs out
// (a day) or they sign out, so pasting the link into a new tab opens it
// without another code. One that has run out is cleared as it is found.
const sessionKey = (link: string) => `klaris_share_session:${link}`;

export function savedViewerSession(link: string): { token: string; email: string; expires_at: string } | null {
  try {
    const raw = localStorage.getItem(sessionKey(link));
    const saved = raw ? JSON.parse(raw) : null;
    if (saved && new Date(saved.expires_at).getTime() > Date.now()) return saved;
    if (raw) localStorage.removeItem(sessionKey(link));
    return null;
  } catch {
    return null;
  }
}

export function saveViewerSession(link: string, session: { token: string; email: string; expires_at: string }): void {
  try {
    localStorage.setItem(sessionKey(link), JSON.stringify(session));
  } catch {
    // Private mode or storage off: the viewer signs in again next time.
  }
}

export function forgetViewerSession(link: string): void {
  try {
    localStorage.removeItem(sessionKey(link));
  } catch {
    // Nothing kept, nothing to forget.
  }
}
